<div class="px-5 py-4" [class.read-only-mode]="isReadOnly()">
  <!-- Toast -->
  <p-toast role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Screen Readers -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <!-- Back Button -->
  <div class="mb-4">
    <button
      type="button"
      class="back-button-custom p-2 border-round flex align-items-center gap-2"
      (click)="navigateBack()"
      [attr.aria-label]="getBackButtonLabel()"
    >
      <i class="pi pi-arrow-left" aria-hidden="true"></i>
      <span class="font-semibold">{{ getBackButtonLabel() }}</span>
    </button>
  </div>

  <!-- Page Title (hidden in review mode) -->
  @if (!isReviewMode()) {
    <div class="surface-0 border-round p-5 mb-4 shadow-1">
      <div class="flex justify-content-between align-items-start">
        <div>
          <h1 class="text-3xl font-bold m-0" style="color: #00125b">
            <i class="pi pi-file-edit mr-3" aria-hidden="true"></i>
            Questionari di valutazione dei rischi rispetto agli standard AXA e informazioni per la predisposizione del contratto
          </h1>
          <p class="text-600 mt-2 mb-0">Vendor Risk Framework - Valutazione dei Rischi del Fiduciario</p>
        </div>
        <!-- Completion Indicator -->
        <div class="completion-badge" [attr.aria-label]="isVRFCompleted() ? 'Questionario completato' : 'Questionario non completato'">
          @if (isVRFCompleted()) {
            <i class="pi pi-check-circle completed-icon" aria-hidden="true"></i>
            <span class="completion-text">Completato</span>
          }
          @else {
            <i class="pi pi-circle incomplete-icon" aria-hidden="true"></i>
            <span class="completion-text">Da completare</span>
          }
        </div>
      </div>
    </div>
  }

  <!-- Instructions -->
  @if (!isReadOnly()) {
    <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="vrf-instructions">
      <p id="vrf-instructions" class="text-sm text-700 m-0">
        <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
        <strong>Istruzioni:</strong> Compila tutti i campi obbligatori contrassegnati con asterisco (*).
      </p>
    </div>
  }

  <!-- Read-Only Mode Banner (hidden in review mode) -->
  @if (isReadOnly() && !isReviewMode()) {
    <div class="read-only-banner" role="alert">
      <div class="flex align-items-center gap-3 mb-2">
        <i class="pi pi-eye" aria-hidden="true"></i>
        <strong>Modalità Sola Lettura</strong>
      </div>
      <span>Stai visualizzando l'anagrafica inviata. I campi non sono modificabili.</span>
    </div>
  }

  <!-- SEZIONE 1: RESPONSABILE DEL TRATTAMENTO -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-1">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-id-card text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-1" class="text-2xl font-semibold m-0 text-900">Responsabile del Trattamento</h2>
        <p class="text-600 m-0 mt-1">Dati del responsabile e DPO/referente privacy</p>
        <p class="text-600 text-sm m-0 mt-2">
          Indicare l'indirizzo e-mail del responsabile del trattamento o del referente privacy.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <div class="field col-12 md:col-6">
        <label for="resp-company" class="font-semibold block mb-2">
          Ragione sociale del Responsabile del trattamento
          <span class="text-red-500" aria-hidden="true">*</span>
          <i
            class="pi pi-info-circle ml-2 text-primary cursor-help"
            pTooltip="Il dato è precompilato con la Ragione sociale del Rappresentante Legale. Modificalo se il Responsabile del trattamento è diverso dal Rappresentante Legale."
            tooltipPosition="top"
            tabindex="0"
            role="img"
            aria-label="Informazioni sulla Ragione sociale del Responsabile del trattamento"
          ></i>
        </label>
        <input
          id="resp-company"
          type="text"
          pInputText
          [(ngModel)]="responsibleCompanyName"
          placeholder="Ragione sociale"
          [disabled]="isReadOnly()"
          [class]="'w-full' + (isFieldInvalid('responsibleCompanyName') ? ' ng-invalid ng-dirty' : '')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('responsibleCompanyName')"
          [attr.aria-describedby]="isFieldInvalid('responsibleCompanyName') ? 'resp-company-error' : null"
        />
        @if (isFieldInvalid('responsibleCompanyName')) {
          <small id="resp-company-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('responsibleCompanyName') }}
          </small>
        }
      </div>

      <div class="field col-12 md:col-3">
        <label for="dpo-cognome" class="font-semibold block mb-2">Cognome del DPO/referente privacy</label>
        <input
          id="dpo-cognome"
          type="text"
          pInputText
          [(ngModel)]="dpoLastName"
          placeholder="Cognome"
          [disabled]="isReadOnly()"
          class="w-full"
        />
      </div>

      <div class="field col-12 md:col-3">
        <label for="dpo-nome" class="font-semibold block mb-2">Nome del DPO/referente privacy</label>
        <input
          id="dpo-nome"
          type="text"
          pInputText
          [(ngModel)]="dpoFirstName"
          placeholder="Nome"
          [disabled]="isReadOnly()"
          class="w-full"
        />
      </div>

      <div class="field col-12 md:col-6">
        <label for="dpo-email" class="font-semibold block mb-2">
          Mail del DPO/referente privacy
          <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="dpo-email"
          type="email"
          pInputText
          [(ngModel)]="dpoEmail"
          (ngModelChange)="onDpoEmailChange($event)"
          placeholder="email@esempio.it"
          [disabled]="isReadOnly()"
          [class]="'w-full' + (isFieldInvalid('dpoEmail') ? ' ng-invalid ng-dirty' : '')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('dpoEmail')"
          [attr.aria-describedby]="isFieldInvalid('dpoEmail') ? 'dpo-email-error' : null"
        />
        @if (isFieldInvalid('dpoEmail')) {
          <small id="dpo-email-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('dpoEmail') }}
          </small>
        }
      </div>
    </div>
  </section>

  <!-- SEZIONE 2: CATEGORIE DI DESTINATARI -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-2">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-users text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-2" class="text-2xl font-semibold m-0 text-900">Dati Trattati UE / Extra UE</h2>
        <p class="text-600 m-0 mt-1">Categorie di destinatari e trasferimenti fuori UE</p>
        <p class="text-600 text-sm m-0 mt-2">
          Specificare brevemente le categorie dei soggetti a cui i dati di AXA sono comunicati, ad esempio fornitori, autorità pubbliche o società del gruppo, per le finalità di trattamento. In caso di trasferimento dati fuori UE, indicare le garanzie applicate per legittimare il trasferimento, ad esempio BCR, clausole contrattuali tipo della Commissione UE o giudizio di adeguatezza dello stato importatore dei dati.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <div class="field col-12">
        <label for="categorie-dest" class="font-semibold block mb-2">
          Categorie di Destinatari (UE ed Extra UE)
          <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <p-multiselect
          id="categorie-dest"
          [(ngModel)]="dataRecipientsCategories"
          [options]="dataRecipientsCategoriesOptions()"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleziona categorie (multiplo)"
          [showClear]="true"
          class="w-full"
          [disabled]="isReadOnly()"
          [class]="isFieldInvalid('dataRecipientsCategories') ? 'ng-invalid ng-dirty' : ''"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('dataRecipientsCategories')"
          [attr.aria-describedby]="isFieldInvalid('dataRecipientsCategories') ? 'categorie-dest-error' : null"
        ></p-multiselect>
        @if (isFieldInvalid('dataRecipientsCategories')) {
          <small id="categorie-dest-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('dataRecipientsCategories') }}
          </small>
        }
      </div>

      @if (dataRecipientsCategories().includes('OTHER')) {
        <div class="field col-12">
          <label for="categorie-dest-altro" class="font-semibold block mb-2">
            Specificare "Altro" per i destinatati UE/extra UE
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="categorie-dest-altro"
            type="text"
            pInputText
            [(ngModel)]="dataRecipientsCategoriesOther"
            placeholder="Specificare altro"
            [disabled]="isReadOnly()"
            [class]="'w-full' + (isFieldInvalid('dataRecipientsCategoriesOther') ? ' ng-invalid ng-dirty' : '')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldInvalid('dataRecipientsCategoriesOther')"
            [attr.aria-describedby]="isFieldInvalid('dataRecipientsCategoriesOther') ? 'categorie-dest-altro-error' : null"
          />
          @if (isFieldInvalid('dataRecipientsCategoriesOther')) {
            <small id="categorie-dest-altro-error" class="text-red-500 block mt-1" role="alert">
              {{ getFieldError('dataRecipientsCategoriesOther') }}
            </small>
          }
        </div>
      }

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('dataTransferOutsideEu')" [class.border-red-500]="isFieldInvalid('dataTransferOutsideEu')" [class.border-round]="isFieldInvalid('dataTransferOutsideEu')" [class.p-2]="isFieldInvalid('dataTransferOutsideEu')">
        <legend class="font-semibold block mb-2">
          I dati di AXA vengono inviati, trasmessi o salvati in archivi anche cloud o call center di fiduciari
          che archiviano documenti fuori dall'UE?
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
        <div class="flex gap-3" role="group" aria-label="Trasferimento dati fuori UE" [class.invalid-radio-group]="isFieldInvalid('dataTransferOutsideEu')">
          <div class="flex align-items-center gap-2" [class.invalid-radio-option]="isFieldInvalid('dataTransferOutsideEu')">
            <p-radiobutton
              name="dataTransferOutsideEu"
              [value]="true"
              [ngModel]="dataTransferOutsideEu()"
              (ngModelChange)="dataTransferOutsideEu.set($event)"
              [disabled]="isReadOnly()"
              inputId="transfer-eu-si"
              [styleClass]="isFieldInvalid('dataTransferOutsideEu') ? 'p-invalid' : ''"
              ariaLabel="Sì"
            />
            <label for="transfer-eu-si" class="cursor-pointer">Sì</label>
          </div>
          <div class="flex align-items-center gap-2" [class.invalid-radio-option]="isFieldInvalid('dataTransferOutsideEu')">
            <p-radiobutton
              name="dataTransferOutsideEu"
              [value]="false"
              [ngModel]="dataTransferOutsideEu()"
              (ngModelChange)="dataTransferOutsideEu.set($event)"
              [disabled]="isReadOnly()"
              inputId="transfer-eu-no"
              [styleClass]="isFieldInvalid('dataTransferOutsideEu') ? 'p-invalid' : ''"
              ariaLabel="No"
            />
            <label for="transfer-eu-no" class="cursor-pointer">No</label>
          </div>
        </div>
        @if (isFieldInvalid('dataTransferOutsideEu')) {
          <small class="text-red-500" role="alert">{{ getFieldError('dataTransferOutsideEu') }}</small>
        }
      </fieldset>

      @if (dataTransferOutsideEu() === true) {
        <fieldset class="field col-12 md:col-6">
          <legend class="font-semibold block mb-2">
            In caso di trasferimento fuori UE, la vostra organizzazione ha adottato misure per garantire
            il rispetto della vigente normativa (es. BCR, clausole contrattuali standard)?
            <span class="text-red-500" aria-hidden="true">*</span>
          </legend>
          <div class="flex gap-3" role="group" aria-label="Misure GDPR trasferimento fuori UE">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="gdprComplianceMeasures"
                [value]="true"
                [ngModel]="gdprComplianceMeasures()"
                (ngModelChange)="gdprComplianceMeasures.set($event)"
                [disabled]="isReadOnly()"
                inputId="gdpr-measures-si"
                ariaLabel="Sì"
              />
              <label for="gdpr-measures-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="gdprComplianceMeasures"
                [value]="false"
                [ngModel]="gdprComplianceMeasures()"
                (ngModelChange)="gdprComplianceMeasures.set($event)"
                [disabled]="isReadOnly()"
                inputId="gdpr-measures-no"
                ariaLabel="No"
              />
              <label for="gdpr-measures-no" class="cursor-pointer">No</label>
            </div>
          </div>
        </fieldset>

        <div class="field col-12">
          <div class="flex align-items-start gap-3 p-3 border-round border-1 border-orange-300 surface-100">
            <i class="pi pi-exclamation-triangle text-2xl text-orange-500 mt-1 flex-shrink-0" aria-hidden="true"></i>
            <div>
              <p class="font-semibold text-orange-700 m-0 mb-1">Azione richiesta</p>
              <p class="text-700 m-0">
                In caso di trasferimento fuori UE è obbligatorio compilare il questionario al seguente link:
              </p>
              <a href="https://forms.office.com/r/C6iX4BVQiD" target="_blank" rel="noopener noreferrer"
                 class="text-primary font-bold underline mt-2 inline-flex align-items-center gap-1">
                <i class="pi pi-external-link" aria-hidden="true"></i>
                Apri il questionario Extra UE
              </a>
            </div>
          </div>
        </div>
      }
    </div>
  </section>

  <!-- SEZIONE 3: CONSERVAZIONE E CANCELLAZIONE -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-3">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-trash text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-3" class="text-2xl font-semibold m-0 text-900">Conservazione e Cancellazione dei Dati</h2>
        <p class="text-600 m-0 mt-1">Modalità di gestione dati AXA</p>
        <p class="text-600 text-sm m-0 mt-2">
          INDICARE le modalità di cancellazione dei dati di AXA dai vostri device (PC/tablet ecc.) al termine del periodo di data retention.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <div class="field col-12 md:col-6">
        <label for="modalita-canc" class="font-semibold block mb-2">
          Modalità cancellazione dei dati AXA (eventualmente salvati su sistemi / pc)
          <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <p-select
          id="modalita-canc"
          [(ngModel)]="dataDeletionMethod"
          [options]="dataDeletionMethodOptions()"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleziona modalità"
          [filter]="true"
          filterPlaceholder="Cerca modalità"
          [showClear]="true"
          [disabled]="isReadOnly()"
          [styleClass]="'w-full' + (isFieldInvalid('dataDeletionMethod') ? ' p-invalid' : '')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('dataDeletionMethod')"
          [attr.aria-describedby]="isFieldInvalid('dataDeletionMethod') ? 'modalita-canc-error' : null"
        ></p-select>
        @if (isFieldInvalid('dataDeletionMethod')) {
          <small id="modalita-canc-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('dataDeletionMethod') }}
          </small>
        }
      </div>
    </div>
  </section>

  <!-- SEZIONE 4: STRUMENTI UTILIZZATI -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-4">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-desktop text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-4" class="text-2xl font-semibold m-0 text-900">Strumenti Utilizzati per il Trattamento</h2>
        <p class="text-600 m-0 mt-1">Programmi IT e gestionali</p>
        <p class="text-600 text-sm m-0 mt-2">
          Indicare il nome di eventuali programmi IT o gestionali operativi dove vengono salvati dati di clienti AXA, oltre a SOL.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <div class="field col-12 md:col-6">
        <label for="programmi-it" class="font-semibold block mb-2">
          Programmi IT/gestionale operativo
          <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="programmi-it"
          type="text"
          pInputText
          [ngModel]="itSystemsUsed()"
          readonly
          class="w-full"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('itSystemsUsed')"
          [attr.aria-describedby]="isFieldInvalid('itSystemsUsed') ? 'programmi-it-error' : null"
        />
        @if (isFieldInvalid('itSystemsUsed')) {
          <small id="programmi-it-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('itSystemsUsed') }}
          </small>
        }
      </div>

      <div class="field col-12 md:col-6">
        <label for="programmi-altro" class="font-semibold block mb-2">
          Altri strumenti utilizzati
        </label>
        <input
          id="programmi-altro"
          type="text"
          pInputText
          [(ngModel)]="itSystemsOther"
          placeholder="Specifica eventuali altri strumenti"
          [disabled]="isReadOnly()"
          class="w-full"
        />
      </div>
    </div>
  </section>

  <!-- SEZIONE 5: MISURE DI SICUREZZA -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-5">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-lock text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-5" class="text-2xl font-semibold m-0 text-900">Misure di Sicurezza Tecniche e Organizzative</h2>
        <p class="text-600 m-0 mt-1">Tutte le misure sono obbligatorie</p>
        <p class="text-600 text-sm m-0 mt-2">
          Specificare quali sono le misure di sicurezza tecniche e organizzative adottate. Indicare nella cella delle risposte quali delle misure sotto riportate sono state adottate; se sono tutte presenti, indicarle tutte.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('credentialsAccess')" [class.border-red-500]="isFieldInvalid('credentialsAccess')" [class.border-round]="isFieldInvalid('credentialsAccess')" [class.p-2]="isFieldInvalid('credentialsAccess')">
        <legend class="font-semibold block mb-2">
          Accesso ai sistemi AXA solo tramite credenziali di autenticazione
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Accesso credenziali">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="credentialsAccess"
                [value]="true"
                [ngModel]="credentialsAccess()"
                (ngModelChange)="credentialsAccess.set($event)"
                [disabled]="isReadOnly()"
                inputId="accesso-cred-si"
                ariaLabel="Sì"
              />
              <label for="accesso-cred-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="credentialsAccess"
                [value]="false"
                [ngModel]="credentialsAccess()"
                (ngModelChange)="credentialsAccess.set($event)"
                [disabled]="isReadOnly()"
                inputId="accesso-cred-no"
                ariaLabel="No"
              />
              <label for="accesso-cred-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('credentialsAccess')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('softwareUpdates')" [class.border-red-500]="isFieldInvalid('softwareUpdates')" [class.border-round]="isFieldInvalid('softwareUpdates')" [class.p-2]="isFieldInvalid('softwareUpdates')">
        <legend class="font-semibold block mb-2">
          Aggiornamenti software
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Aggiornamenti software">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="softwareUpdates"
                [value]="true"
                [ngModel]="softwareUpdates()"
                (ngModelChange)="softwareUpdates.set($event)"
                [disabled]="isReadOnly()"
                inputId="agg-software-si"
                ariaLabel="Sì"
              />
              <label for="agg-software-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="softwareUpdates"
                [value]="false"
                [ngModel]="softwareUpdates()"
                (ngModelChange)="softwareUpdates.set($event)"
                [disabled]="isReadOnly()"
                inputId="agg-software-no"
                ariaLabel="No"
              />
              <label for="agg-software-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('softwareUpdates')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('passwordUpdates')" [class.border-red-500]="isFieldInvalid('passwordUpdates')" [class.border-round]="isFieldInvalid('passwordUpdates')" [class.p-2]="isFieldInvalid('passwordUpdates')">
        <legend class="font-semibold block mb-2">
          Aggiornamento periodico password
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Aggiornamento password">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="passwordUpdates"
                [value]="true"
                [ngModel]="passwordUpdates()"
                (ngModelChange)="passwordUpdates.set($event)"
                [disabled]="isReadOnly()"
                inputId="agg-password-si"
                ariaLabel="Sì"
              />
              <label for="agg-password-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="passwordUpdates"
                [value]="false"
                [ngModel]="passwordUpdates()"
                (ngModelChange)="passwordUpdates.set($event)"
                [disabled]="isReadOnly()"
                inputId="agg-password-no"
                ariaLabel="No"
              />
              <label for="agg-password-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('passwordUpdates')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('antivirusFirewall')" [class.border-red-500]="isFieldInvalid('antivirusFirewall')" [class.border-round]="isFieldInvalid('antivirusFirewall')" [class.p-2]="isFieldInvalid('antivirusFirewall')">
        <legend class="font-semibold block mb-2">
          Antivirus-sistema di firewall-anti malware
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Antivirus firewall">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="antivirusFirewall"
                [value]="true"
                [ngModel]="antivirusFirewall()"
                (ngModelChange)="antivirusFirewall.set($event)"
                [disabled]="isReadOnly()"
                inputId="antivirus-si"
                ariaLabel="Sì"
              />
              <label for="antivirus-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="antivirusFirewall"
                [value]="false"
                [ngModel]="antivirusFirewall()"
                (ngModelChange)="antivirusFirewall.set($event)"
                [disabled]="isReadOnly()"
                inputId="antivirus-no"
                ariaLabel="No"
              />
              <label for="antivirus-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('antivirusFirewall')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('periodicBackup')" [class.border-red-500]="isFieldInvalid('periodicBackup')" [class.border-round]="isFieldInvalid('periodicBackup')" [class.p-2]="isFieldInvalid('periodicBackup')">
        <legend class="font-semibold block mb-2">
          Salvataggio periodico dei dati
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Salvataggio dati">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="periodicBackup"
                [value]="true"
                [ngModel]="periodicBackup()"
                (ngModelChange)="periodicBackup.set($event)"
                [disabled]="isReadOnly()"
                inputId="salvataggio-si"
                ariaLabel="Sì"
              />
              <label for="salvataggio-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="periodicBackup"
                [value]="false"
                [ngModel]="periodicBackup()"
                (ngModelChange)="periodicBackup.set($event)"
                [disabled]="isReadOnly()"
                inputId="salvataggio-no"
                ariaLabel="No"
              />
              <label for="salvataggio-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('periodicBackup')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('staffTraining')" [class.border-red-500]="isFieldInvalid('staffTraining')" [class.border-round]="isFieldInvalid('staffTraining')" [class.p-2]="isFieldInvalid('staffTraining')">
        <legend class="font-semibold block mb-2">
          Formazione dei soggetti autorizzati al trattamento
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Formazione soggetti">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="staffTraining"
                [value]="true"
                [ngModel]="staffTraining()"
                (ngModelChange)="staffTraining.set($event)"
                [disabled]="isReadOnly()"
                inputId="formazione-si"
                ariaLabel="Sì"
              />
              <label for="formazione-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="staffTraining"
                [value]="false"
                [ngModel]="staffTraining()"
                (ngModelChange)="staffTraining.set($event)"
                [disabled]="isReadOnly()"
                inputId="formazione-no"
                ariaLabel="No"
              />
              <label for="formazione-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('staffTraining')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>

      <fieldset class="field col-12 md:col-6" [class.border-1]="isFieldInvalid('authorizedPersonnelList')" [class.border-red-500]="isFieldInvalid('authorizedPersonnelList')" [class.border-round]="isFieldInvalid('authorizedPersonnelList')" [class.p-2]="isFieldInvalid('authorizedPersonnelList')">
        <legend class="font-semibold block mb-2">
          Predisposizione di un elenco dei soggetti autorizzati al trattamento
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
          <div class="flex gap-3" role="group" aria-label="Elenco soggetti">
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="authorizedPersonnelList"
                [value]="true"
                [ngModel]="authorizedPersonnelList()"
                (ngModelChange)="authorizedPersonnelList.set($event)"
                [disabled]="isReadOnly()"
                inputId="elenco-si"
                ariaLabel="Sì"
              />
              <label for="elenco-si" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="authorizedPersonnelList"
                [value]="false"
                [ngModel]="authorizedPersonnelList()"
                (ngModelChange)="authorizedPersonnelList.set($event)"
                [disabled]="isReadOnly()"
                inputId="elenco-no"
                ariaLabel="No"
              />
              <label for="elenco-no" class="cursor-pointer">No</label>
            </div>
          </div>
          @if (isFieldInvalid('authorizedPersonnelList')) {
            <small class="text-red-500" role="alert">Campo obbligatorio</small>
          }
      </fieldset>
    </div>
  </section>

  <!-- SEZIONE 6: SUB-RESPONSABILI -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-6">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-sitemap text-2xl" style="color: #00125b" aria-hidden="true"></i>
      <div>
        <h2 id="section-6" class="text-xl font-semibold m-0" style="color: #00125b">Sub-responsabili</h2>
        <p class="text-600 text-sm m-0 mt-2">
          Per sub-responsabile si intende una terza parte o fornitore a cui vengono trasmesse informazioni e/o dati di AXA. Se sono presenti sub-responsabili, rispondere alle domande che seguono.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <fieldset class="field col-12">
        <legend class="font-semibold block mb-2">
          Sono presenti sub-responsabili?
        </legend>
        <div class="flex gap-3" role="group" aria-label="Sub-responsabili presenti">
          <div class="flex align-items-center gap-2">
            <p-radiobutton
              name="hasSubResponsibles"
              [value]="true"
              [(ngModel)]="hasSubResponsibles"
              [disabled]="isReadOnly()"
              inputId="ha-sub-si"
              ariaLabel="Sì"
            />
            <label for="ha-sub-si" class="cursor-pointer">Sì</label>
          </div>
          <div class="flex align-items-center gap-2">
            <p-radiobutton
              name="hasSubResponsibles"
              [value]="false"
              [(ngModel)]="hasSubResponsibles"
              [disabled]="isReadOnly()"
              inputId="ha-sub-no"
              ariaLabel="No"
            />
            <label for="ha-sub-no" class="cursor-pointer">No</label>
          </div>
        </div>
      </fieldset>
    </div>

    @if (hasSubResponsibles()) {
      <div class="grid">
        <!-- Header con descrizione -->
        <div class="col-12">
          <div class="mb-3">
            <h3 class="text-lg font-semibold m-0">Elenco Sub-responsabili</h3>
            <small class="text-muted">Terze parti cui vengono trasmesse informazioni di AXA</small>
          </div>
        </div>

        <!-- Messaggio quando non ci sono sub-responsabili -->
        @if (subResponsibles().length === 0) {
          <div class="col-12">
            <p class="text-center p-4 border-round text-muted surface-100">
              <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
              Nessun sub-responsabile aggiunto. Clicca "Aggiungi Sub-responsabile" per iniziare.
            </p>
          </div>
        }

        <!-- Iterazione sui sub-responsabili -->
        @for (sub of subResponsibles(); track sub.id; let idx = $index) {
          <div class="col-12">
            <div class="surface-50 border-round p-4 mb-3">
              <div class="flex justify-content-between align-items-center mb-3">
                <h4 class="text-md font-semibold m-0">
                  <i class="pi pi-sitemap mr-2" aria-hidden="true"></i>
                  Sub-responsabile #{{ idx + 1 }}
                </h4>
                @if (!isReadOnly()) {
                  <p-button
                    icon="pi pi-trash"
                    severity="danger"
                    size="small"
                    [text]="true"
                    (onClick)="removeSubResponsible(sub.id)"
                    [attr.aria-label]="'Rimuovi sub-responsabile ' + (idx + 1)"
                  ></p-button>
                }
              </div>

          <div class="formgrid grid">
            <div class="field col-12 md:col-6">
              <label [for]="'ragione-sub-' + sub.id" class="font-semibold block mb-2">
                Ragione sociale
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                [id]="'ragione-sub-' + sub.id"
                type="text"
                pInputText
                [(ngModel)]="sub.companyName"
                placeholder="Ragione sociale"
                [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-companyName') ? ' ng-invalid ng-dirty' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-companyName')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-companyName') ? 'ragione-sub-' + sub.id + '-error' : null"
              />
              @if (isFieldInvalid('sub-' + idx + '-companyName')) {
                <small [id]="'ragione-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-companyName') }}
                </small>
              }
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'nazione-sub-' + sub.id" class="font-semibold block mb-2">
                Nazione (Sede legale)
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                [id]="'nazione-sub-' + sub.id"
                [(ngModel)]="sub.headquartersCountry"
                (onChange)="onSubResponsibleCountryChange(sub.id, $event.value)"
                [options]="countriesOptions()"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleziona nazione"
                [filter]="true"
                filterPlaceholder="Cerca nazione"
                [showClear]="true"
                [disabled]="isReadOnly()"
                [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-headquartersCountry') ? ' p-invalid' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-headquartersCountry')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-headquartersCountry') ? 'nazione-sub-' + sub.id + '-error' : null"
              ></p-select>
              @if (isFieldInvalid('sub-' + idx + '-headquartersCountry')) {
                <small [id]="'nazione-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-headquartersCountry') }}
                </small>
              }
            </div>

            <!-- Region: Dropdown for Italy, Text for foreign countries -->
            @if (sub.headquartersCountry === 'IT') {
              <div class="field col-12 md:col-4">
                <label [for]="'regione-sub-' + sub.id" class="font-semibold block mb-2">
                  Regione
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p class="text-600 text-sm m-0 mb-2">Inizia selezionando la regione.</p>
                <p-select
                  [id]="'regione-sub-' + sub.id"
                  [(ngModel)]="sub.operatingRegion"
                  (onChange)="onSubResponsibleRegionChange(sub.id, $event.value)"
                  [options]="subResponsibleRegionsOptions().get(sub.id!) || []"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleziona regione"
                  [filter]="true"
                  filterPlaceholder="Cerca regione"
                  [showClear]="true"
                  [disabled]="isReadOnly()"
                  [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingRegion') ? ' p-invalid' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingRegion')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingRegion') ? 'regione-sub-' + sub.id + '-error' : null"
                ></p-select>
                @if (isFieldInvalid('sub-' + idx + '-operatingRegion')) {
                  <small [id]="'regione-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingRegion') }}
                  </small>
                }
              </div>
            } @else {
              <div class="field col-12 md:col-4">
                <label [for]="'regione-sub-' + sub.id" class="font-semibold block mb-2">
                  Regione
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  [id]="'regione-sub-' + sub.id"
                  type="text"
                  pInputText
                  [(ngModel)]="sub.operatingRegion"
                  placeholder="Regione"
                  [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingRegion') ? ' ng-invalid ng-dirty' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingRegion')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingRegion') ? 'regione-sub-' + sub.id + '-error' : null"
                />
                @if (isFieldInvalid('sub-' + idx + '-operatingRegion')) {
                  <small [id]="'regione-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingRegion') }}
                  </small>
                }
              </div>
            }

            <!-- Province: Dropdown for Italy (disabled until region selected), Text for foreign countries -->
            @if (sub.headquartersCountry === 'IT') {
              <div class="field col-12 md:col-4">
                <label [for]="'provincia-sub-' + sub.id" class="font-semibold block mb-2">
                  Provincia
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p class="text-600 text-sm m-0 mb-2">Prima seleziona la regione.</p>
                <p-select
                  [id]="'provincia-sub-' + sub.id"
                  [(ngModel)]="sub.operatingProvince"
                  (onChange)="onSubResponsibleProvinceChange(sub.id, $event.value)"
                  [options]="subResponsibleProvincesOptions().get(sub.id!) || []"
                  [disabled]="isReadOnly() || !sub.operatingRegion"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleziona provincia"
                  [filter]="true"
                  filterPlaceholder="Cerca provincia"
                  [showClear]="true"
                  [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingProvince') ? ' p-invalid' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingProvince')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingProvince') ? 'provincia-sub-' + sub.id + '-error' : null"
                ></p-select>
                @if (isFieldInvalid('sub-' + idx + '-operatingProvince')) {
                  <small [id]="'provincia-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingProvince') }}
                  </small>
                }
              </div>
            } @else {
              <div class="field col-12 md:col-4">
                <label [for]="'provincia-sub-' + sub.id" class="font-semibold block mb-2">
                  Provincia
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  [id]="'provincia-sub-' + sub.id"
                  type="text"
                  pInputText
                  [(ngModel)]="sub.operatingProvince"
                  placeholder="Provincia"
                  [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingProvince') ? ' ng-invalid ng-dirty' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingProvince')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingProvince') ? 'provincia-sub-' + sub.id + '-error' : null"
                />
                @if (isFieldInvalid('sub-' + idx + '-operatingProvince')) {
                  <small [id]="'provincia-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingProvince') }}
                  </small>
                }
              </div>
            }

            <!-- Municipality: Dropdown for Italy (disabled until province selected), Text for foreign countries -->
            @if (sub.headquartersCountry === 'IT') {
              <div class="field col-12 md:col-6">
                <label [for]="'comune-sub-' + sub.id" class="font-semibold block mb-2">
                  Comune
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <p class="text-600 text-sm m-0 mb-2">Prima seleziona la provincia.</p>
                <p-select
                  [id]="'comune-sub-' + sub.id"
                  [(ngModel)]="sub.operatingMunicipality"
                  [options]="subResponsibleMunicipalitiesOptions().get(sub.id!) || []"
                  [disabled]="isReadOnly() || !sub.operatingProvince"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Seleziona comune"
                  [filter]="true"
                  filterPlaceholder="Cerca comune"
                  [showClear]="true"
                  [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingMunicipality') ? ' p-invalid' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingMunicipality')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingMunicipality') ? 'comune-sub-' + sub.id + '-error' : null"
                ></p-select>
                @if (isFieldInvalid('sub-' + idx + '-operatingMunicipality')) {
                  <small [id]="'comune-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingMunicipality') }}
                  </small>
                }
              </div>
            } @else {
              <div class="field col-12 md:col-6">
                <label [for]="'comune-sub-' + sub.id" class="font-semibold block mb-2">
                  Comune
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  [id]="'comune-sub-' + sub.id"
                  type="text"
                  pInputText
                  [(ngModel)]="sub.operatingMunicipality"
                  placeholder="Comune"
                  [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-operatingMunicipality') ? ' ng-invalid ng-dirty' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-operatingMunicipality')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-operatingMunicipality') ? 'comune-sub-' + sub.id + '-error' : null"
                />
                @if (isFieldInvalid('sub-' + idx + '-operatingMunicipality')) {
                  <small [id]="'comune-sub-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-operatingMunicipality') }}
                  </small>
                }
              </div>
            }

            <div class="field col-12 md:col-6">
              <label [for]="'luogo-tratt-' + sub.id" class="font-semibold block mb-2">
                Luogo in cui sono trattati i dati
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                [id]="'luogo-tratt-' + sub.id"
                [(ngModel)]="sub.dataProcessingLocation"
                [options]="dataProcessingLocationOptions()"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleziona luogo"
                [filter]="true"
                filterPlaceholder="Cerca luogo"
                [showClear]="true"
                [disabled]="isReadOnly()"
                [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-dataProcessingLocation') ? ' p-invalid' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-dataProcessingLocation')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-dataProcessingLocation') ? 'luogo-tratt-' + sub.id + '-error' : null"
              ></p-select>
              @if (isFieldInvalid('sub-' + idx + '-dataProcessingLocation')) {
                <small [id]="'luogo-tratt-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-dataProcessingLocation') }}
                </small>
              }
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'luogo-spec-' + sub.id" class="font-semibold block mb-2">
                Luogo specifico in cui sono trattati i dati.
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-select
                [id]="'luogo-spec-' + sub.id"
                [(ngModel)]="sub.specificLocationLegalBasis"
                [options]="countriesOptions()"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleziona paese"
                [showClear]="true"
                [disabled]="isReadOnly()"
                [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-specificLocationLegalBasis') ? ' p-invalid' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-specificLocationLegalBasis')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-specificLocationLegalBasis') ? 'luogo-spec-' + sub.id + '-error' : null"
              ></p-select>
              @if (isFieldInvalid('sub-' + idx + '-specificLocationLegalBasis')) {
                <small [id]="'luogo-spec-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-specificLocationLegalBasis') }}
                </small>
              }
            </div>

            @if (sub.dataProcessingLocation === 'EXTRA_EU') {
              <div class="field col-12">
                <label [for]="'garanzie-' + sub.id" class="font-semibold block mb-2">
                  Quali garanzie sono applicate al trasferimento dati EXTRA UE?
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  [id]="'garanzie-' + sub.id"
                  type="text"
                  pInputText
                  [(ngModel)]="sub.extraEuTransferSafeguards"
                  placeholder="es. BCR, clausole contrattuali tipo della commissione UE, giudizio di adeguatezza dello stato importatore dei dati)"
                  [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-extraEuTransferSafeguards') ? ' ng-invalid ng-dirty' : '')"
                  [attr.aria-required]="true"
                  [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-extraEuTransferSafeguards')"
                  [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-extraEuTransferSafeguards') ? 'garanzie-' + sub.id + '-error' : null"
                />
                @if (isFieldInvalid('sub-' + idx + '-extraEuTransferSafeguards')) {
                  <small [id]="'garanzie-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                    {{ getFieldError('sub-' + idx + '-extraEuTransferSafeguards') }}
                  </small>
                }
              </div>
            }

            <div class="field col-12">
              <label [for]="'tipologia-dati-' + sub.id" class="font-semibold block mb-2">
                Categoria dati trattati
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <p-multiselect
                [id]="'tipologia-dati-' + sub.id"
                [(ngModel)]="sub.dataTypesProcessed"
                [options]="dataTypesOptions()"
                optionLabel="label"
                optionValue="value"
                placeholder="Seleziona tipologie"
                [filter]="true"
                filterPlaceholder="Cerca tipologia"
                [styleClass]="'w-full' + (isFieldInvalid('sub-' + idx + '-dataTypesProcessed') ? ' p-invalid' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-dataTypesProcessed')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-dataTypesProcessed') ? 'tipologia-dati-' + sub.id + '-error' : null"
              ></p-multiselect>
              @if (isFieldInvalid('sub-' + idx + '-dataTypesProcessed')) {
                <small [id]="'tipologia-dati-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-dataTypesProcessed') }}
                </small>
              }
            </div>

            @if (sub.dataTypesProcessed.includes('OTHER')) {
              <div class="field col-12">
                <label [for]="'tipologia-dati-altro-' + sub.id" class="font-semibold block mb-2">
                  Descrizione altro dato trattato
                  <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  [id]="'tipologia-dati-altro-' + sub.id"
                  type="text"
                  pInputText
                  [(ngModel)]="sub.dataTypesProcessedOther"
                  placeholder="Descrivi il tipo di dato trattato"
                  [disabled]="isReadOnly()"
                  class="w-full"
                />
              </div>
            }

            <div class="field col-12">
              <label [for]="'desc-processi-' + sub.id" class="font-semibold block mb-2">
                Breve descrizione dei processi affidati al Sub-responsabile
                <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                [id]="'desc-processi-' + sub.id"
                type="text"
                pInputText
                [(ngModel)]="sub.processesDescription"
                placeholder="Descrizione dei processi"
                [class]="'w-full' + (isFieldInvalid('sub-' + idx + '-processesDescription') ? ' ng-invalid ng-dirty' : '')"
                [attr.aria-required]="true"
                [attr.aria-invalid]="isFieldInvalid('sub-' + idx + '-processesDescription')"
                [attr.aria-describedby]="isFieldInvalid('sub-' + idx + '-processesDescription') ? 'desc-processi-' + sub.id + '-error' : null"
              />
              @if (isFieldInvalid('sub-' + idx + '-processesDescription')) {
                <small [id]="'desc-processi-' + sub.id + '-error'" class="text-red-500 block mt-1" role="alert">
                  {{ getFieldError('sub-' + idx + '-processesDescription') }}
                </small>
              }
            </div>
          </div>
            </div>
          </div>
        }

        <!-- Pulsante aggiungi sub-responsabile -->
        @if (!isReadOnly()) {
          <div class="col-12">
            <div class="flex justify-content-end">
              <p-button
                label="Aggiungi Sub-responsabile"
                icon="pi pi-plus"
                (onClick)="addSubResponsible()"
              ></p-button>
            </div>
          </div>
        }
      </div>
    }
  </section>

  <!-- SEZIONE 7: AMMINISTR ATORI DI SISTEMA -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-7">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-cog text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-7" class="text-2xl font-semibold m-0 text-900">Informazioni sugli Amministratori di Sistema</h2>
        <p class="text-600 m-0 mt-1">Specificare presenza elenco amministratori</p>
        <p class="text-600 text-sm m-0 mt-2">
          Elencare gli amministratori di sistema preposti, se presenti. In caso contrario, scrivere NA.
        </p>
        <p class="text-600 text-sm m-0 mt-2">
          Se non applicabile inserire NA; se presenti, specificare che presso la sede legale e presente elenco degli amministratori di sistema.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <div class="field col-12">
        <label for="info-amm" class="font-semibold block mb-2">Informazioni sugli amministratori di sistema
          <span class="text-red-500" aria-hidden="true">*</span>
        </label>
        <input
          id="info-amm"
          type="text"
          pInputText
          [(ngModel)]="systemAdministratorsInfo"
          placeholder="Se non applicabile inserire 'NA', altrimenti specificare che presso la sede legale è presente elenco"
          [class]="'w-full' + (isFieldInvalid('systemAdministratorsInfo') ? ' ng-invalid ng-dirty' : '')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="isFieldInvalid('systemAdministratorsInfo')"
          [attr.aria-describedby]="isFieldInvalid('systemAdministratorsInfo') ? 'info-amm-error' : null"
        />
        @if (isFieldInvalid('systemAdministratorsInfo')) {
          <small id="info-amm-error" class="text-red-500 block mt-1" role="alert">
            {{ getFieldError('systemAdministratorsInfo') }}
          </small>
        }
      </div>
    </div>
  </section>

  <!-- SEZIONE 8: CLOUD -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-8">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-cloud text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-8" class="text-2xl font-semibold m-0 text-900">Informazioni sul Cloud</h2>
        <p class="text-600 m-0 mt-1">Utilizzo del cloud per dati AXA</p>
        <p class="text-600 text-sm m-0 mt-2">
          Indicare se i dati AXA vengono salvati su cloud. In caso affermativo specificare il tipo di cloud, ad esempio cloud pubblico, privato, ibrido, PaaS, IaaS o SaaS.
        </p>
      </div>
    </div>

    <div class="formgrid grid">
      <fieldset class="field col-12">
        <legend class="font-semibold block mb-2">
          I dati AXA vengono salvati sul cloud?
          <span class="text-red-500" aria-hidden="true">*</span>
        </legend>
        <div class="flex gap-3" role="group" aria-label="Dati salvati sul cloud">
          <div class="flex align-items-center gap-2">
            <p-radiobutton
              name="cloudStorageUsed"
              [value]="true"
              [(ngModel)]="cloudStorageUsed"
              [disabled]="isReadOnly()"
              inputId="dati-cloud-si"
              ariaLabel="Sì"
            />
            <label for="dati-cloud-si" class="cursor-pointer">Sì</label>
          </div>
          <div class="flex align-items-center gap-2">
            <p-radiobutton
              name="cloudStorageUsed"
              [value]="false"
              [(ngModel)]="cloudStorageUsed"
              [disabled]="isReadOnly()"
              inputId="dati-cloud-no"
              ariaLabel="No"
            />
            <label for="dati-cloud-no" class="cursor-pointer">No</label>
          </div>
        </div>
      </fieldset>

      @if (cloudStorageUsed()) {
        <div class="field col-12">
          <label for="tipo-cloud" class="font-semibold block mb-2">
            Specificare il tipo di cloud (es. Cloud Pubblico, Cloud Privato, Ibrido, PAAS, IAAS, SAAS)
            <span class="text-red-500" aria-hidden="true">*</span>
          </label>
          <input
            id="tipo-cloud"
            type="text"
            pInputText
            [(ngModel)]="cloudType"
            placeholder="Inserisci il tipo di cloud utilizzato"
            maxlength="500"
            [disabled]="isReadOnly()"
            [class]="'w-full' + (isFieldInvalid('cloudType') ? ' ng-invalid ng-dirty' : '')"
            [attr.aria-required]="true"
            [attr.aria-invalid]="isFieldInvalid('cloudType')"
            [attr.aria-describedby]="isFieldInvalid('cloudType') ? 'tipo-cloud-error' : null"
          />
          @if (isFieldInvalid('cloudType')) {
            <small id="tipo-cloud-error" class="text-red-500 block mt-1" role="alert">
              {{ getFieldError('cloudType') }}
            </small>
          }
        </div>
      }
    </div>
  </section>

  <!-- SAVE BUTTONS -->
  <div class="form-actions mt-6">
    @if (isReadOnly()) {
      <div class="actions-left">
        <button
          pButton
          type="button"
          label="Torna Indietro"
          icon="pi pi-arrow-left"
          class="p-button-outlined"
          (click)="navigateBack()"
        ></button>
      </div>
      <div class="actions-right">
        <!-- Empty for read-only mode, could add PDF download later -->
      </div>
    } @else {
      <div class="actions-left">
        <button
          pButton
          type="button"
          label="Torna Indietro"
          icon="pi pi-arrow-left"
          class="p-button-outlined p-button-secondary"
          (click)="navigateBack()"
        ></button>
      </div>
      
      <div class="actions-right">
        <button
          pButton
          type="button"
          label="Salva Bozza"
          icon="pi pi-save"
          class="p-button-outlined"
          [loading]="isSaving()"
          (click)="saveDraft()"
        ></button>
        <button
          pButton
          type="button"
          label="Salva e prosegui"
          icon="pi pi-check"
          class="p-button-primary"
          [loading]="isSaving()"
          (click)="save()"
        ></button>
      </div>
    }
  </div>
</div>
